import _ from "lodash";
import React, { useState, useMemo } from "react";

import { Modal, Button, IconButton, EmptyState } from "@toddle-edu/ds-web";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";
import { TeacherAddOutlined } from "@toddle-edu/ds-icons";

import ViewMembers from "./components/ViewMembers";
import AddMembersSearch from "./components/AddMembersSearch";
import RemoveUserConfirmationModal from "./components/RemoveUserConfirmationModal";
import classes from "./ManageMembersModal.scss";
import {
  addMembersInChannel,
  handleRemoveUserFromChannel,
  getGradeUsers,
} from "MessagingV2/modules/MessagingModule";
import { useI18n } from "Hooks";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { isIdEqual } from "Utils";
import { connect } from "react-redux";
import classNames from "classnames";

const styles = {
  modalBodyStyles: {
    paddingTop: "4px",
    paddingRight: "0px",
    overflow: "hidden",
  },
  searchOverlayContainerStyle: {
    maxHeight: "356px",
  },
  emptyStateSubtitleStyle: {
    width: "380px",
  },
};

const ManageMembersModal = props => {
  const {
    onCloseModal,
    users,
    chatId,
    addMembersInChannel,
    handleRemoveUserFromChannel,
    channelLabel,
    isMemberUpdate,
    isUpdateFlow = false,
  } = props;

  const { t } = useI18n(["common"]);

  const [isAddMembers, setIsAddMembers] = useState(false);
  const [selectedAddUsers, setSelectedAddUsers] = useState([]);

  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);

  // remove user modal states
  const [openRemoveUserModal, setOpenRemoveUserModal] = useState(false);
  const [removeUser, setRemoveUser] = useState(null);
  const [isRemoveButtonDisable, setIsRemoveButtonDisable] = useState(false);
  const [loadingState, setLoadingState] = useState({});
  const showMembersList = !(isAddMembers && _.isEmpty(selectedAddUsers));

  const handleAddMemberUI = () => {
    setIsAddMembers(true);
  };

  const handleAddUsers = users => {
    setSelectedAddUsers(state => {
      const userList = _.map(users, user => {
        return _.pick(user, ["id", "type"]);
      });
      const allUsers = [...state, ...userList];
      return _.uniqBy(allUsers, "id");
    });
  };

  const handleModalClose = () => {
    if (isAddMembers) {
      setIsAddMembers(false);
    } else {
      onCloseModal();
    }
  };

  const handleUpdateUsers = async () => {
    setIsSubmitButtonDisabled(true);
    const isSuccess = await addMembersInChannel({
      chatId,
      users: _.concat(selectedAddUsers, users),
    });
    if (isSuccess) {
      setSelectedAddUsers([]);
    }
    setIsSubmitButtonDisabled(false);
    handleModalClose();
  };

  const handleRemoveUser = async ({ user }) => {
    setIsRemoveButtonDisable(true);

    const updatedCurrentUsers = _.filter(users, u => {
      return !isIdEqual(user.id, u.id);
    });
    handleRemoveUserFromChannel({
      chatId,
      users: updatedCurrentUsers,
      removedUser: user,
    });
    handleRemoveUserModalClose();
    setIsRemoveButtonDisable(false);
  };

  const getIsButtonDisabled = () => {
    return _.isEmpty(selectedAddUsers) || isSubmitButtonDisabled;
  };

  const excludedUsersIds = useMemo(() => {
    const excludedUsers = [...users, ...selectedAddUsers];
    return _.map(excludedUsers, user => user.id);
  }, [users, selectedAddUsers]);

  const handleRemoveUserModalClose = () => {
    setOpenRemoveUserModal(false);
  };

  const handleAddGrade = async ({ gradeId, userType }) => {
    if (!gradeId || !userType) return;
    const loaderKey = `${userType}_${gradeId}`;
    if (loadingState[loaderKey]) return;
    setLoadingState(state => ({ ...state, [loaderKey]: true }));
    // api call to get user ids
    const userIds = await getGradeUsers({ gradeId, userType });

    if (!_.isEmpty(userIds)) handleAddUsers(userIds);
    setLoadingState(state => _.omit(state, loaderKey));
  };

  const isLoading = _.some(loadingState, value => value);

  const loadingStateByType = useMemo(
    () =>
      _.reduce(
        loadingState,
        (result, value, key) => {
          const firstWord = key.split("_")[0];
          result[firstWord] = result[firstWord] || value;
          return result;
        },
        {}
      ),
    [loadingState]
  );

  if (openRemoveUserModal) {
    return (
      <RemoveUserConfirmationModal
        channelLabel={channelLabel}
        handleRemoveUser={handleRemoveUser}
        onCloseModal={handleRemoveUserModalClose}
        user={removeUser}
        isButtonDisabled={isRemoveButtonDisable}
      />
    );
  }

  const showAddMemberCountInFooter = !_.isEmpty(selectedAddUsers);

  return (
    <Modal
      width="576px"
      height="620px"
      isClose={false}
      onClose={handleModalClose}
    >
      <Modal.Header>
        <div className={classes.modalHeaderContainer}>
          <div className={classNames("heading-4", classes.manageMemberHeader)}>
            {t("common:manage_members_for_label", { label: channelLabel })}
          </div>
        </div>
        {!isAddMembers && isMemberUpdate ? (
          <div className={classes.addMemberIconContainer}>
            <IconButton
              icon={<TeacherAddOutlined />}
              variant={"plain"}
              onClick={handleAddMemberUI}
            />
          </div>
        ) : null}
      </Modal.Header>
      <Modal.Body style={styles.modalBodyStyles}>
        {isAddMembers ? (
          <div className={classes.addMembersSearchContainer}>
            <AddMembersSearch
              handleAddUsers={handleAddUsers}
              handleAddGrade={handleAddGrade}
              channelLabel={channelLabel}
              excludedUserIds={excludedUsersIds}
              isManageMember={true}
              overlayContainerStyle={styles.searchOverlayContainerStyle}
            />
          </div>
        ) : null}
        {showMembersList || isLoading ? (
          <ViewMembers
            users={isAddMembers ? selectedAddUsers : users}
            updateUsers={setSelectedAddUsers}
            chatId={chatId}
            handleRemoveUser={handleRemoveUser}
            isAddMembers={isAddMembers}
            loadingStateByType={loadingStateByType}
            isMemberUpdate={isMemberUpdate}
          />
        ) : (
          <div className={classes.emptyState}>
            <EmptyState
              illustration={NoSearchInFilterIllustration}
              subtitle={t("common:search_for_member_grade_class_to_add")}
              subtitleStyle={styles.emptyStateSubtitleStyle}
              size="small"
            />
          </div>
        )}
      </Modal.Body>
      {isAddMembers ? (
        <Modal.Footer hasSeparator>
          <div className={classes.modalButtonContainer}>
            {showAddMemberCountInFooter ? (
              <div
                className={classNames(
                  "text-body-1",
                  classes.addMemberCountText
                )}
              >
                {t("common:label_selected", {
                  label: t("member_count_lowercase", {
                    count: _.size(selectedAddUsers),
                  }),
                })}
              </div>
            ) : null}

            <Button
              size="medium"
              variant="progressive"
              onClick={handleUpdateUsers}
              disabled={getIsButtonDisabled()}
            >
              {t("common:add_members")}
            </Button>
          </div>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
};

const mapActionCreators = {
  addMembersInChannel,
  handleRemoveUserFromChannel,
  setToastMsg,
};

export default connect(null, mapActionCreators)(ManageMembersModal);
